// ========================================
// INTERFACES DE RONDAS (PATROL)
// ========================================

/**
 * Status dos checkpoints da ronda
 */
export enum PatrolCheckpointStatus {
    PENDING = 'pending',
    COMPLETED = 'completed',
    SKIPPED = 'skipped',
}

/**
 * Status da ronda - Alinhado com o enum do backend (Prisma)
 */
export enum PatrolStatus {
    IDLE = 'IDLE',
    STARTED = 'STARTED',
    PAUSED = 'PAUSED',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
}

/**
 * Interface para checkpoint de ronda
 */
export interface IPatrolCheckpoint {
    readonly id: string; // ID do PatrolCheckpoint (relacionamento)
    readonly checkpointId?: string; // ID do Checkpoint base (usado para atualização no backend)
    readonly name: string;
    readonly latitude: number;
    readonly longitude: number;
    readonly required: boolean;
    readonly status: PatrolCheckpointStatus;
    readonly notes?: string;
    readonly completedAt?: Date;
    readonly order?: number;
    readonly distanceFromOriginal?: number; // Distância do checkpoint original em metros (quando marcado)
}

/**
 * Interface base para entidade de ronda
 */
export interface IPatrolResponse {
    readonly id: string;
    readonly status: PatrolStatus;
    readonly startTime?: Date;
    readonly endTime?: Date;
    readonly description?: string;
    readonly userId: string;
    readonly companyId: string;
    readonly checkpoints: IPatrolCheckpoint[];
    readonly supervisorApproval?: boolean;
    readonly createdAt?: Date;
    readonly updatedAt?: Date;
    readonly deletedAt?: Date;

    // Relacionamentos
    readonly user?: {
        id: string;
        name: string;
        email: string;
    };
    readonly company?: {
        id: string;
        name: string;
    };
}

/**
 * DTO para criação de ronda
 */
export interface ICreatePatrolDto {
    readonly description?: string;
    readonly shiftId?: string; // Opcional, pode vir do token
    readonly companyId?: string; // Opcional, pode vir do token
    readonly postId?: string; // Opcional, pode vir do token
    readonly checkpoints?: Omit<IPatrolCheckpoint, 'id' | 'status' | 'completedAt'>[];
}

/**
 * DTO para atualização de ronda
 */
export interface IUpdatePatrolDto {
    readonly status?: PatrolStatus;
    readonly description?: string;
    readonly endTime?: Date;
    readonly supervisorApproval?: boolean;
    readonly checkpoints?: IPatrolCheckpoint[];
}

/**
 * DTO para atualização de checkpoint
 * Nota: O status deve ser enviado em maiúsculas (PENDING, COMPLETED, SKIPPED) para compatibilidade com o backend
 */
export interface IUpdateCheckpointDto {
    readonly status: PatrolCheckpointStatus | 'PENDING' | 'COMPLETED' | 'SKIPPED';
    readonly notes?: string;
    readonly completedAt?: Date;
    readonly latitude?: number;
    readonly longitude?: number;
    readonly accuracy?: number;
}

/**
 * Filtros para busca de rondas
 */
export interface IPatrolFilters {
    readonly page?: number;
    readonly limit?: number;
    readonly search?: string;
    readonly status?: PatrolStatus;
    readonly userId?: string;
    readonly companyId?: string;
    readonly startDate?: string;
    readonly endDate?: string;
    readonly supervisorApproval?: boolean;
}

/**
 * Interface para resposta de paginação de rondas
 */
export interface IPatrolPaginationResponse {
    readonly data: IPatrolResponse[];
    readonly pagination: {
        readonly page: number;
        readonly limit: number;
        readonly total: number;
        readonly totalPages: number;
        readonly hasNextPage: boolean;
        readonly hasPreviousPage: boolean;
    };
}

/**
 * Interface para métricas de ronda
 */
export interface IPatrolMetrics {
    readonly totalPatrols: number;
    readonly completedPatrols: number;
    readonly pendingPatrols: number;
    readonly cancelledPatrols: number;
    readonly averageDuration: number; // em minutos
    readonly checkpointsCompleted: number;
    readonly checkpointsTotal: number;
    readonly completionRate: number; // percentual
}

/**
 * Interface para resumo de ronda atual
 */
export interface ICurrentPatrol {
    readonly id: string;
    readonly status: PatrolStatus;
    readonly startTime: Date;
    readonly endTime?: Date;
    readonly userId: string;
    readonly progress: {
        readonly completed: number;
        readonly total: number;
        readonly percentage: number;
    };
}
